import { useEffect, useMemo, useState } from 'react';
import { portfolioApi } from '@/services/portfolio-api';
import type { IProductTimeSeriesResponse } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { TrendCard } from './TrendCard';
import type { ITrendPoint } from './TrendLineChart';
import { buildBucketAxisProps, todayIsoUtc } from '@/lib/metric-view/bucket-format';

function pivotByType(res: IProductTimeSeriesResponse): ITrendPoint[] {
  return res.series.map((point) => ({
    week: point.period,
    Story: point.values.Story ?? 0,
    Bug: point.values.Bug ?? 0,
    Task: point.values.Task ?? 0,
  }));
}

interface IThroughputTrendCardProps {
  filters: IMetricViewFilters;
}

export function ThroughputTrendCard({ filters }: Readonly<IThroughputTrendCardProps>) {
  const [data, setData] = useState<IProductTimeSeriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    portfolioApi.getThroughputTrend({
      from: filters.from,
      to: filters.to,
      granularity: 'weekly',
      groupBy: 'type',
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
      includeArchived: filters.includeArchived || undefined,
    }).then((res) => {
      if (controller.signal.aborted) return;
      setData(res);
      setLoading(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load throughput trend.');
      setLoading(false);
    });

    return () => controller.abort();
  }, [filters.from, filters.to, filters.productId, filters.teamId, filters.includeArchived]);

  const trendData = useMemo(() => (data ? pivotByType(data) : []), [data]);
  const activeType = filters.issueType === 'epic' ? 'all' : filters.issueType;
  const axis = useMemo(
    () => buildBucketAxisProps({
      today: todayIsoUtc(),
      rangeFrom: filters.from,
      rangeTo: filters.to,
    }),
    [filters.from, filters.to],
  );

  return (
    <TrendCard
      title="Throughput"
      description="Items closed per week, by issue type."
      data={trendData}
      activeType={activeType}
      loading={loading}
      error={error}
      axis={axis}
    />
  );
}
